import * as uuid from 'uuid'

import { InternalWindow, MobileSDKAction, MobileSDKMessage, NativeHookMessage } from './types'

interface MobileSDKMessagingParams {
  componentName: string
  componentVersion: string
}

/**
 * Class responsible for initializing bidirectional exchange of signals
 * between WebSDK and Mobile SDK to allow Native features such as fullscreeen view
 */
class MobileSDKMessaging {
  private readonly MSG_BRIDGE_SCRIPT_URL =
    'https://x.klarnacdn.net/mobile-sdk/mobile-js-snippet/v1/app.min.js'

  private sender: string
  private receiver: string
  private componentVersion: string
  private pendingMessages: Map<
    string,
    { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }
  >

  constructor({ componentName, componentVersion }: MobileSDKMessagingParams) {
    this.sender = componentName
    this.componentVersion = componentVersion
    this.receiver = 'Native'
    this.pendingMessages = new Map()
  }

  /**
   * Checks if the library runs in a Mobile SDK context(e.g. WebView integrated with Mobile SDK).
   * @param win - The window object.
   */
  static isMobileSDKContext(
    { win }: { win: globalThis.Window & Window & InternalWindow } = {
      win: window,
    }
  ): boolean {
    if (
      win.webkit &&
      win.webkit.messageHandlers &&
      win.webkit.messageHandlers.KlarnaNativeHookMessageHandler
    ) {
      // If iOS' WKWebView
      return true
      // If iOS' UIWebView or Android's WebView
    } else if (win.KlarnaNativeHookMessageHandler) {
      return true
    }
    return false
  }

  /**
   * Initializes handshake with Mobile SDK
   */
  async handshake() {
    // load messaging bridge script
    await this.loadMessagingBridgeScript()
    // add message receiver
    this.addMessageReceiver()

    return this.send({
      action: MobileSDKAction.HANDSHAKE,
      data: {
        componentName: this.sender,
        componentVersion: this.componentVersion,
      },
    })
  }

  /**
   * Sends URL to Mobile SDK to open in a separate fullscreen
   * @param url safe page URL
   */
  loadSeparateFullscreenUrl(url: string) {
    return this.send({
      action: MobileSDKAction.LOAD_FULLSCREEN_URL,
      data: {
        url,
      },
    })
  }

  /**
   * Sends a signal to open separate fullscreen native view
   */
  showSeparateFullscreen() {
    this.send({
      action: MobileSDKAction.FULLSCREEN_SHOW,
      data: {
        placement: 'full',
      },
    })
  }

  /**
   * Sends a signal to hide separate fullscreen in native view
   */
  hideSeparateFullscreen() {
    this.send({
      action: MobileSDKAction.FULLSCREEN_HIDE,
      data: {},
    })
  }

  /**
   * Lazy loads Mobile SDK bridge script to enable signal exchange
   */
  private loadMessagingBridgeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if the script is already loaded
      if (document.querySelector(`script[src="${this.MSG_BRIDGE_SCRIPT_URL}"]`)) {
        // Verify that __KlarnaNativeHook is available even if script was already loaded
        if (!window.__KlarnaNativeHook) {
          reject(
            new Error('Mobile SDK bridge script loaded but __KlarnaNativeHook is not available')
          )
          return
        }
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = this.MSG_BRIDGE_SCRIPT_URL

      script.onload = () => {
        // Verify that __KlarnaNativeHook is available after script load
        if (!window.__KlarnaNativeHook) {
          reject(
            new Error('Mobile SDK bridge script loaded but __KlarnaNativeHook is not available')
          )
          return
        }
        resolve()
      }

      script.onerror = () => {
        reject(new Error(`Failed to load script: ${this.MSG_BRIDGE_SCRIPT_URL}`))
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Enables WebSDK sender to receive signal responses from Mobile SDK
   */
  private addMessageReceiver() {
    window.__KlarnaNativeHook!.addReceiver(this.sender, (msg: NativeHookMessage) => {
      const messageId = msg.messageId

      if (this.pendingMessages.has(messageId)) {
        const { resolve, reject } = this.pendingMessages.get(messageId)!

        // error handling for specific signal responses
        if (
          [
            'fullscreenLoadUrlResponse',
            'fullscreenShowResponse',
            'fullscreenHideResponse',
          ].includes(msg.action)
        ) {
          const isSuccess = Boolean(msg.params['success'])

          if (isSuccess) {
            resolve(msg)
          } else {
            reject(new Error('Failed to perform action in Mobile SDK'))
          }
        } else {
          resolve(msg)
        }

        this.pendingMessages.delete(messageId)
      }
    })
  }

  /**
   * Sends a signal to Mobile SDK
   */
  private send<T, K>({ action, data }: MobileSDKMessage<T>): Promise<K> {
    return new Promise((resolve, reject) => {
      const messageId = uuid.v4()

      this.pendingMessages.set(messageId, { resolve, reject })

      window.__KlarnaNativeHook!.postMessage({
        action,
        messageId,
        sender: this.sender,
        receiver: this.receiver,
        params: { ...data },
      })
    })
  }
}

export default MobileSDKMessaging
